/**
 * CaseMaker Generated Document Types
 *
 * Frontend type definitions for CaseMaker document generation.
 * Mirrors the backend types in functions/src/types/generatedDoc.ts
 */

// Document generation types
export const DOCUMENT_GENERATION_TYPES = [
  'insurance_appeal',
  'prior_authorization',
  'iep_parent_input',
  'iep_accommodation_request',
  'ssdi_narrative',
  'medicaid_application',
  'provider_letter',
  'records_request',
  'custom',
  'appointment_prep',
] as const;

export type DocumentGenerationType = typeof DOCUMENT_GENERATION_TYPES[number];

// Document status values
export const DOCUMENT_STATUSES = [
  'draft',
  'generating',
  'ready',
  'exported',
  'submitted',
] as const;

export type DocumentStatus = typeof DOCUMENT_STATUSES[number];

// Submission methods
export const SUBMISSION_METHODS = [
  'mail',
  'fax',
  'email',
  'portal',
  'in_person',
] as const;

export type SubmissionMethod = typeof SUBMISSION_METHODS[number];

// Export formats
export const EXPORT_FORMATS = ['pdf', 'docx'] as const;

export type ExportFormat = typeof EXPORT_FORMATS[number];

// Export artifacts (FEAT-003)
export const EXPORT_ARTIFACTS = ['letter', 'citations', 'bundle'] as const;

export type ExportArtifact = typeof EXPORT_ARTIFACTS[number];

// ============================================================
// Citation Report Types (FEAT-003 / ADR-019)
// Mirrors functions/src/types/citation.ts
// ============================================================

export const CONFIDENCE_LEVELS = ['direct', 'inferred', 'contextual'] as const;
export type ConfidenceLevel = typeof CONFIDENCE_LEVELS[number];

export const VERIFICATION_STATUSES = ['verified', 'needs_review', 'unsupported'] as const;
export type VerificationStatus = typeof VERIFICATION_STATUSES[number];

export const SOURCE_TYPES = [
  'source_document',
  'care_log',
  'case_file_field',
  'deadline',
  'user_description',
  'additional_context',
] as const;
export type SourceType = typeof SOURCE_TYPES[number];

/**
 * A single citation row — one factual claim mapped to one source.
 * Multi-source claims emit multiple rows sharing the same claimId.
 */
export interface Citation {
  claimId: string;
  claimText: string;
  sourceType: SourceType;
  /** Real Firestore ID for source_document/care_log/deadline/case_file_field;
   * sentinel string ('USER_DESCRIPTION', 'ADDITIONAL_CONTEXT', 'UNMAPPED') otherwise. */
  sourceId: string;
  sourceLabel?: string;
  excerpt: string;
  confidence: ConfidenceLevel;
  verification: VerificationStatus;
  reviewerNote?: string;
}

/**
 * Citation summary counts (mirrors functions/src/types/citation.ts).
 *
 * The four primary counts are CLAIM-LEVEL (distinct claimId values) using
 * a worst-status rollup. `citationRowCount` is the raw row total — useful
 * for diagnostics, NOT for "claims cited" labels.
 */
export interface CitationSummary {
  /** Claims whose every source row is verified. */
  verifiedCount: number;
  /** Claims with no unsupported rows but at least one needs_review row. */
  needsReviewCount: number;
  /** Claims with at least one unsupported source row. */
  unsupportedCount: number;
  /** Distinct claim count (= sum of the three buckets above). */
  totalCount: number;
  /** Raw row count (each multi-source claim contributes >1 row). */
  citationRowCount: number;
  /** BUG-119: AI rows whose declared sourceType was off-enum (tolerated by
   * the BUG-118 parse, then normalized). 0 on a clean response. */
  offEnumSourceTypeCount: number;
}

export interface CitationReport {
  citations: Citation[];
  summary: CitationSummary;
  /** Set when the letter has been edited since these citations were generated.
   * UI shows a "may be outdated" banner; regeneration is the only way to clear. */
  stale: boolean;
  generatedAt: Date | { seconds: number; nanoseconds: number };
  modelUsed?: string;
}

/**
 * Custom document details for freeform document generation
 */
export interface CustomDocumentDetails {
  title: string;
  purpose: string;
  recipient?: string;
  recipientRole?: string;
  tone: 'formal' | 'professional' | 'assertive' | 'collaborative';
  keyPoints: string;
  desiredOutcome: string;
}

/**
 * Recipient information for letters
 */
export interface RecipientInfo {
  name?: string;
  title?: string;
  organization?: string;
  address?: string;
  faxNumber?: string;
  email?: string;
}

/**
 * Export record
 */
export interface DocumentExport {
  exportId: string;
  format: ExportFormat;
  exportedAt: Date | { seconds: number; nanoseconds: number };
  storagePath: string;
  downloadUrl?: string;
  urlExpiresAt?: Date | { seconds: number; nanoseconds: number };
}

/**
 * Generated Document - full document data
 */
export interface GeneratedDocument {
  docId: string;
  userId: string;
  caseFileId: string;
  documentType: DocumentGenerationType;
  title: string;
  status: DocumentStatus;
  sourceDocumentIds: string[];
  sourceCareLogIds: string[];
  sourceDeadlineId?: string;
  userDescription: string;
  additionalContext?: string;
  templateVersion: string;
  generatedContent: string;
  editedContent?: string;
  hasBeenEdited: boolean;
  recipientInfo?: RecipientInfo;
  exports: DocumentExport[];
  submittedAt?: Date | { seconds: number; nanoseconds: number };
  submissionMethod?: SubmissionMethod;
  submissionNotes?: string;
  confirmationNumber?: string;
  createdAt: Date | { seconds: number; nanoseconds: number };
  updatedAt: Date | { seconds: number; nanoseconds: number };
  createdBy: string;
  lastModifiedBy: string;
  modelUsed: string;
  generationDurationMs?: number;
  tokenCount?: number;
  estimatedCostUsd?: number;
  /** FEAT-003 / ADR-019 — Source Citation Report. Absent on legacy docs
   * and on generations that fell back to the markdown-only path. */
  citationReport?: CitationReport | null;
  /** BUG-118 — true when the letter is not backed by usable grounding
   * (markdown fallback, or zero verified claims). Mirrors the server flag;
   * absent on legacy docs, so treat only `=== true` as degraded. Drives the
   * export-tier predictor in lockstep with the server gate. */
  degraded?: boolean;
}

/**
 * Generated Document Summary - for list views
 */
export interface GeneratedDocumentSummary {
  docId: string;
  caseFileId: string;
  documentType: DocumentGenerationType;
  title: string;
  status: DocumentStatus;
  hasBeenEdited: boolean;
  createdAt: Date | { seconds: number; nanoseconds: number };
  updatedAt: Date | { seconds: number; nanoseconds: number };
  submittedAt?: Date | { seconds: number; nanoseconds: number };
}

// ============================================================
// Request/Response Types for API calls
// ============================================================

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface GenerateDocRequest {
  caseFileId: string;
  documentType: DocumentGenerationType;
  userDescription: string;
  additionalContext?: string;
  sourceDocumentIds?: string[];
  sourceCareLogIds?: string[];
  sourceDeadlineId?: string;
  recipientInfo?: RecipientInfo;
  dateRange?: DateRange;
  /** Inclusive start date for care log filtering (YYYY-MM-DD). Required for appointment_prep. */
  dateRangeStart?: string;
  /** Inclusive end date for care log filtering (YYYY-MM-DD). Required for appointment_prep. */
  dateRangeEnd?: string;
  /** Appointment type (e.g., "Pediatric Neurology"). Required for appointment_prep. */
  appointmentType?: string;
  /** Appointment date (YYYY-MM-DD). Required for appointment_prep. */
  appointmentDate?: string;
  /** Provider or team name (e.g., "Dr. Chen"). Required for appointment_prep. */
  providerName?: string;
  title?: string;
  customDocumentDetails?: CustomDocumentDetails;
}

export interface GenerateDocResponse {
  success: boolean;
  docId?: string;
  status?: DocumentStatus;
  generatedContent?: string;
  title?: string;
  error?: string;
}

export interface UpdateGeneratedDocRequest {
  docId: string;
  editedContent?: string;
  title?: string;
  recipientInfo?: RecipientInfo;
  status?: 'draft' | 'ready' | 'submitted';
  submissionMethod?: SubmissionMethod;
  submissionNotes?: string;
  confirmationNumber?: string;
}

export interface UpdateGeneratedDocResponse {
  success: boolean;
  document?: GeneratedDocument;
  error?: string;
}

export interface ListGeneratedDocsRequest {
  caseFileId?: string;
  status?: DocumentStatus;
  documentType?: DocumentGenerationType;
  limit?: number;
  startAfter?: string;
}

export interface ListGeneratedDocsResponse {
  success: boolean;
  documents: GeneratedDocumentSummary[];
  totalCount: number;
  lastDocumentId?: string;
  error?: string;
}

export interface GetGeneratedDocRequest {
  docId: string;
}

export interface GetGeneratedDocResponse {
  success: boolean;
  document?: GeneratedDocument;
  caseFileName?: string;
  error?: string;
}

export interface RegenerateDocRequest {
  docId: string;
  updatedUserDescription?: string;
  updatedAdditionalContext?: string;
}

export interface RegenerateDocResponse {
  success: boolean;
  docId?: string;
  generatedContent?: string;
  error?: string;
}

// ============================================================
// IMPL-002 — Layer 4 export acknowledgment
// Mirrors functions/src/types/generatedDoc.ts
// ============================================================

export const EXPORT_ACK_TYPES = ['standard', 'elevated_null_citations'] as const;
export type ExportAckType = typeof EXPORT_ACK_TYPES[number];

/**
 * Document version reference for the acknowledgment binding (IMPL-002 PR-3).
 * Integer second/nanosecond pair of the document's `updatedAt` as reviewed;
 * the server compares by strict integer equality and denies with
 * DOCUMENT_CHANGED on mismatch.
 */
export interface ExportDocVersionRef {
  seconds: number;
  nanoseconds: number;
}

/**
 * Convert whatever timestamp shape the wire delivered into an integer
 * version ref. Admin-SDK Timestamps JSON-serialize as `{_seconds,
 * _nanoseconds}` (underscored private fields) even though the local type
 * says `{seconds, nanoseconds}` — handle both, plus Date defensively.
 * Returns null when no integer pair can be derived (caller must not guess).
 */
export function toExportDocVersionRef(
  value: Date | { seconds: number; nanoseconds: number } | undefined | null,
): ExportDocVersionRef | null {
  if (!value) return null;
  if (value instanceof Date) {
    const ms = value.getTime();
    if (!Number.isFinite(ms)) return null;
    return { seconds: Math.floor(ms / 1000), nanoseconds: (ms % 1000) * 1_000_000 };
  }
  const v = value as unknown as Record<string, unknown>;
  const seconds =
    typeof v.seconds === 'number' ? v.seconds :
    typeof v._seconds === 'number' ? v._seconds : null;
  const nanoseconds =
    typeof v.nanoseconds === 'number' ? v.nanoseconds :
    typeof v._nanoseconds === 'number' ? v._nanoseconds : null;
  if (
    seconds === null || nanoseconds === null ||
    !Number.isInteger(seconds) || !Number.isInteger(nanoseconds)
  ) {
    return null;
  }
  return { seconds, nanoseconds };
}

/**
 * Acknowledgment payload required on every export request. The server
 * recomputes the required tier from the document's actual citationReport
 * state and rejects mismatches — the client never decides which tier applies.
 */
export interface ExportAcknowledgment {
  /** Must be literal true. */
  acknowledged: boolean;
  /** Which affirmation the user gave. Must match the server-computed tier. */
  ackType: ExportAckType;
  /** Which copy wording was shown (e.g. '2026-06-draft'). */
  ackCopyVersion: string;
  /** ISO 8601 client timestamp; server enforces a ±10 min skew window. */
  acknowledgedAt: string;
  /** PR-3: binds the acknowledgment to the document version reviewed. */
  docUpdatedAt: ExportDocVersionRef;
}

/** Structured gate-denial codes returned by exportGeneratedDocHttp. */
export const EXPORT_GATE_CODES = [
  'ACK_REQUIRED',
  'ELEVATED_ACK_REQUIRED',
  'DOC_NOT_EXPORTABLE',
  'DOCUMENT_CHANGED',
  'AUDIT_UNAVAILABLE',
] as const;
export type ExportGateCode = typeof EXPORT_GATE_CODES[number];

/**
 * Error thrown by the service layer for non-OK responses, carrying the
 * HTTP status and parsed body so callers can route on structured gate
 * codes instead of message text.
 */
export interface ServiceHttpError extends Error {
  status?: number;
  body?: unknown;
}

/** Structured gate denial extracted from a service-layer error. */
export interface ExportGateDenial {
  code: ExportGateCode;
  requiredAckType?: ExportAckType;
  message?: string;
}

/**
 * Extract a structured export-gate denial from an unknown error, or null
 * when the error is not a gate denial (network failure, rate limit, etc.).
 */
export function getExportGateDenial(err: unknown): ExportGateDenial | null {
  if (!(err instanceof Error)) return null;
  const body = (err as ServiceHttpError).body;
  if (!body || typeof body !== 'object') return null;
  const code = (body as Record<string, unknown>).code;
  if (typeof code !== 'string' ||
      !EXPORT_GATE_CODES.includes(code as ExportGateCode)) {
    return null;
  }
  const requiredAckType = (body as Record<string, unknown>).requiredAckType;
  const message = (body as Record<string, unknown>).error;
  return {
    code: code as ExportGateCode,
    requiredAckType:
      typeof requiredAckType === 'string' &&
      EXPORT_ACK_TYPES.includes(requiredAckType as ExportAckType)
        ? (requiredAckType as ExportAckType)
        : undefined,
    message: typeof message === 'string' ? message : undefined,
  };
}

export interface ExportGeneratedDocRequest {
  docId: string;
  format: ExportFormat;
  useEditedContent?: boolean;
  includeLetterhead?: boolean;
  /** FEAT-003 — which artifact to render. Defaults to 'letter'. */
  artifact?: ExportArtifact;
  /** IMPL-002 — required Layer 4 acknowledgment; server rejects without it. */
  acknowledgment?: ExportAcknowledgment;
}

export interface ExportGeneratedDocResponse {
  success: boolean;
  downloadUrl?: string;
  exportId?: string;
  expiresAt?: Date | { seconds: number; nanoseconds: number };
  error?: string;
  /** IMPL-002 — structured gate code on denial. */
  code?: ExportGateCode;
  /** IMPL-002 — present on ack denials so the client renders the right tier. */
  requiredAckType?: ExportAckType;
  /** IMPL-002 — set when post-release bookkeeping failed (non-fatal). */
  warning?: string;
}

export interface DeleteGeneratedDocRequest {
  docId: string;
}

export interface DeleteGeneratedDocResponse {
  success: boolean;
  error?: string;
}

// ============================================================
// Document Type Metadata for UI
// ============================================================

export type DocumentCategory = 'insurance' | 'education' | 'benefits' | 'medical' | 'custom';

export interface DocumentTypeInfo {
  type: DocumentGenerationType;
  name: string;
  description: string;
  icon: string;
  category: DocumentCategory;
  suggestedSources: string[];
  promptHints: string[];
}

export const DOCUMENT_TYPES: DocumentTypeInfo[] = [
  {
    type: 'insurance_appeal',
    name: 'Insurance Appeal Letter',
    description: 'Appeal a denied insurance claim with supporting medical evidence',
    icon: 'FileWarning',
    category: 'insurance',
    suggestedSources: ['denial letter', 'medical records', 'doctor recommendations'],
    promptHints: [
      'What service or treatment was denied?',
      'When did you receive the denial letter?',
      'What reason did they give for the denial?',
      'Has your doctor recommended this treatment?',
    ],
  },
  {
    type: 'prior_authorization',
    name: 'Prior Authorization Request',
    description: 'Request insurance approval before receiving a service or treatment',
    icon: 'ClipboardCheck',
    category: 'insurance',
    suggestedSources: ['doctor prescription', 'medical records', 'diagnosis documentation'],
    promptHints: [
      'What service or treatment needs authorization?',
      'Which doctor prescribed or recommended this?',
      'Why is this treatment medically necessary?',
      'Have alternative treatments been tried?',
    ],
  },
  {
    type: 'iep_parent_input',
    name: 'IEP Parent Input Letter',
    description: 'Share your concerns and observations before an IEP meeting',
    icon: 'GraduationCap',
    category: 'education',
    suggestedSources: ['current IEP', 'progress reports', 'evaluations'],
    promptHints: [
      'When is the IEP meeting scheduled?',
      'What are your main concerns about current progress?',
      'What is working well that should continue?',
      'What changes would you like to discuss?',
    ],
  },
  {
    type: 'iep_accommodation_request',
    name: 'IEP Accommodation Request',
    description: 'Request specific accommodations or modifications to the IEP',
    icon: 'Settings',
    category: 'education',
    suggestedSources: ['current IEP', 'evaluations', 'medical documentation'],
    promptHints: [
      'What specific accommodation are you requesting?',
      'Why does your child need this accommodation?',
      'How will this help them access their education?',
      'What happens without this accommodation?',
    ],
  },
  {
    type: 'ssdi_narrative',
    name: 'SSI/SSDI Application Narrative',
    description: 'Detailed narrative for Social Security disability benefits application',
    icon: 'FileText',
    category: 'benefits',
    suggestedSources: ['medical records', 'doctor letters', 'therapy records', 'evaluations'],
    promptHints: [
      'What are the primary disabling conditions?',
      'How do these conditions affect daily activities?',
      'What treatments are currently being received?',
      'What limitations prevent work or self-care?',
    ],
  },
  {
    type: 'medicaid_application',
    name: 'Medicaid Application/Renewal',
    description: 'Support documentation for Medicaid application or annual renewal',
    icon: 'Heart',
    category: 'benefits',
    suggestedSources: ['medical records', 'diagnosis documentation', 'income documentation'],
    promptHints: [
      'Is this a new application or renewal?',
      'What medical conditions require coverage?',
      'What services are needed?',
      'Has there been any change in circumstances?',
    ],
  },
  {
    type: 'provider_letter',
    name: 'Provider Coordination Letter',
    description: 'Share care information with a healthcare provider or specialist',
    icon: 'Stethoscope',
    category: 'medical',
    suggestedSources: ['medical records', 'care logs', 'medication list'],
    promptHints: [
      'Who is this letter being sent to?',
      'What is the purpose of sharing this information?',
      'What recent changes or concerns should they know about?',
      'What questions do you have for this provider?',
    ],
  },
  {
    type: 'records_request',
    name: 'Medical Records Request',
    description: 'HIPAA-compliant request for medical records from a provider',
    icon: 'FolderOpen',
    category: 'medical',
    suggestedSources: [],
    promptHints: [
      'Which provider or facility has the records?',
      'What date range of records do you need?',
      'What types of records (office visits, lab results, imaging)?',
      'Where should the records be sent?',
    ],
  },
  {
    type: 'custom',
    name: 'Custom Document',
    description: 'Describe what you need and let AI help you draft it',
    icon: 'PenLine',
    category: 'custom',
    suggestedSources: [],
    promptHints: [],
  },
  {
    type: 'appointment_prep',
    name: 'Appointment Prep',
    description: 'Prepare a structured 1-pager with questions, observations, and medication notes for an upcoming visit',
    icon: 'Stethoscope',
    category: 'medical',
    suggestedSources: ['care logs', 'medication list'],
    promptHints: [
      'What type of appointment is this? (e.g., Pediatric Neurology, IEP Meeting)',
      'Who is the provider or team you are meeting with?',
      'When is the appointment?',
    ],
  },
];

// ============================================================
// Status Styling Configuration
// ============================================================

export const STATUS_STYLES: Record<DocumentStatus, { bg: string; text: string; label: string }> = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Draft' },
  generating: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Generating' },
  ready: { bg: 'bg-green-100', text: 'text-green-700', label: 'Ready' },
  exported: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Exported' },
  submitted: { bg: 'bg-teal-100', text: 'text-teal-700', label: 'Submitted' },
};

export const CATEGORY_STYLES: Record<DocumentCategory, { bg: string; text: string; label: string }> = {
  insurance: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Insurance' },
  education: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Education' },
  benefits: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Benefits' },
  medical: { bg: 'bg-rose-100', text: 'text-rose-800', label: 'Medical' },
  custom: { bg: 'bg-violet-100', text: 'text-violet-800', label: 'Custom' },
};

// ============================================================
// Helper Functions
// ============================================================

/**
 * Get document type info by type
 */
export function getDocumentTypeInfo(type: DocumentGenerationType): DocumentTypeInfo | undefined {
  return DOCUMENT_TYPES.find((dt) => dt.type === type);
}

/**
 * Get human-readable document type name
 */
export function getDocumentTypeName(type: DocumentGenerationType): string {
  const info = getDocumentTypeInfo(type);
  return info?.name || type;
}

/**
 * Get document types by category
 */
export function getDocumentTypesByCategory(category: DocumentCategory): DocumentTypeInfo[] {
  return DOCUMENT_TYPES.filter((dt) => dt.category === category);
}

/**
 * Format a Firestore timestamp to a Date object
 */
export function formatTimestamp(
  timestamp: Date | { seconds: number; nanoseconds: number } | undefined
): Date | null {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if ('seconds' in timestamp) {
    return new Date(timestamp.seconds * 1000);
  }
  return null;
}

/**
 * Format a date for display
 */
export function formatDate(date: Date | null): string {
  if (!date) return 'Unknown';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a date with time for display
 */
export function formatDateTime(date: Date | null): string {
  if (!date) return 'Unknown';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Format relative time (e.g., "2 days ago")
 */
export function formatRelativeTime(date: Date | null): string {
  if (!date) return 'Unknown';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

  return formatDate(date);
}

/**
 * Check if document type requires recipient info
 */
export function requiresRecipientInfo(type: DocumentGenerationType): boolean {
  return [
    'insurance_appeal',
    'prior_authorization',
    'provider_letter',
    'records_request',
  ].includes(type);
}
